import * as React from "react";
import { cn } from "@workspace/ui/lib/utils";
import { wasTouchTap } from "../../lib/touch.ts";

// Single source of truth for all Play Entry selection buttons across apps used across the apps.
// Size and width are controlled per call site via `className`; the gray/blue
// palette, border radius, weight, shadow and transitions are owned here.
export interface PracticeButtonProps
  extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  selected?: boolean;
}

export const PracticeButton = React.forwardRef<
  HTMLButtonElement,
  PracticeButtonProps
>(
  (
    { selected = false, className, type = "button", children, onClick, onDoubleClick, disabled, ...props },
    ref,
  ) => {
    const handledByTouchRef = React.useRef(false);
    const lastTapAtRef = React.useRef(0);

    const handleClick = (event: React.MouseEvent<HTMLButtonElement>) => {
      if (handledByTouchRef.current) {
        handledByTouchRef.current = false;
        return;
      }
      onClick?.(event);
    };

    const handleTouchEnd = (event: React.TouchEvent<HTMLButtonElement>) => {
      if (disabled) return;
      // A moved touch is a SCROLL, not a tap — bail before preventDefault so
      // momentum scrolling works and the button doesn't fire while the
      // finger merely passes over it. (A moved touch never produces a
      // native click either, so nothing double-fires.)
      if (!wasTouchTap()) return;
      // Suppress the synthetic mouse/click iOS would fire after touchend, so
      // the button doesn't keep focus and steal the next tap on a sibling element.
      event.preventDefault();
      handledByTouchRef.current = true;

      // Manually detect double-tap so onDoubleClick still fires on touch devices
      // (preventDefault above blocks iOS's synthetic dblclick).
      const now = event.timeStamp || performance.now();
      const synthetic = event as unknown as React.MouseEvent<HTMLButtonElement>;
      if (onDoubleClick && now - lastTapAtRef.current < 400) {
        lastTapAtRef.current = 0;
        onDoubleClick(synthetic);
      } else {
        lastTapAtRef.current = now;
        onClick?.(synthetic);
      }
      // Release focus so the next tap on a different element fires immediately.
      (event.currentTarget as HTMLButtonElement).blur();
    };

    return (
      <button
        ref={ref}
        type={type}
        disabled={disabled}
        data-selected={selected || undefined}
        className={cn(
          "inline-flex touch-manipulation select-none items-center justify-center gap-2 rounded-md px-3 py-2 text-base font-medium",
          "[-webkit-tap-highlight-color:transparent] [&_svg]:pointer-events-none [&_img]:pointer-events-none",
          "disabled:cursor-not-allowed disabled:opacity-50",
          selected
            ? "bg-blue-600 text-white font-semibold shadow-[0px_3px_0px_0px_rgba(255,255,255,0.35)_inset,0px_-1px_0px_0px_rgba(0,0,0,0.15)_inset,0px_2px_3px_0px_rgba(0,0,0,0.2)]"
            : "bg-[#eef1f4] text-[#111827] hover:bg-[#dde2e6] shadow-[0px_3px_0px_0px_#f6f8fa_inset,0px_-1px_0px_0px_rgba(0,0,0,0.08)_inset,0px_2px_3px_0px_rgba(0,0,0,0.18)]",
          className,
        )}
        {...props}
        onClick={handleClick}
        onDoubleClick={onDoubleClick}
        onTouchEnd={handleTouchEnd}
      >
        {children}
      </button>
    );
  },
);

PracticeButton.displayName = "PracticeButton";
